import React from "react";
import { Link } from "react-router-dom";
import { LongPage, TocEntry } from "./SideNav";
import { Bullet, Bullets, Mail, PageHeader, Section } from "./ui";
import { CONTACT_EMAIL } from "./constants";

const TOC: TocEntry[] = [
  { id: "today", label: "Where we are today" },
  { id: "built", label: "What is built" },
  { id: "influence", label: "Write to us" },
];

export const RoadmapPage: React.FC = () => (
  <LongPage
    toc={TOC}
    intro={
      <>
        <PageHeader
          kicker="Roadmap"
          title="Where the system actually stands"
          lead={
            <p>
              A research group deciding whether to put its data in a system needs to know where
              that system actually stands — not where it is hoped to be. So this page describes
              what the system does today.
            </p>
          }
        />
        <div className="mt-10 max-w-3xl rounded-xl border border-amber-200 bg-amber-50 px-6 py-5">
          <p className="font-semibold text-ink">This page is under development</p>
          <p className="site-prose mt-2 text-[0.9375rem]">
            What comes next is still being decided. Rather than publish a plan we have not agreed
            on, this page covers only what exists today. The planned work will appear here once it
            is settled.
          </p>
        </div>
      </>
    }
  >
    <div className="divide-y divide-sand-200 [&>section:not(:first-child)]:pt-14 [&>section]:pb-14 [&>section:last-child]:pb-0">
      <Section id="today" number="01" title="Where we are today">
        <p>
          The system does the thing it exists to do. Data can be uploaded, personal data is found
          and replaced, datasets are catalogued with permissions that hold up, researchers can
          request and receive data, and every step is logged.
        </p>
      </Section>

      <Section id="built" number="02" title="What is built">
        <p>Working today, in short:</p>
        <Bullets tight>
          <Bullet>
            <strong>Pseudonymization</strong> — HMAC-SHA256 under project-scoped keys, with
            original values encrypted at rest so authorized re-identification stays possible and
            logged.
          </Bullet>
          <Bullet>
            <strong>Accounts and access</strong> — invitation-only accounts, mandatory two-factor
            authentication, revocable sessions, and roles for researchers, principal
            investigators, managers, and data providers.
          </Bullet>
          <Bullet>
            <strong>Dataset catalogue</strong> — column metadata and summary statistics, separate
            visibility and download scopes, expiry dates, and bulk download with a ready-to-run
            notebook and R script.
          </Bullet>
          <Bullet>
            <strong>Data requests</strong> — submission with column selection, PI review,
            approval or rejection, and redirection to external data providers.
          </Bullet>
          <Bullet>
            <strong>Upload screening</strong> — personal data detected by column name, content,
            and inside free text, with a human confirmation step before anything is stored.
          </Bullet>
          <Bullet>
            <strong>Expiry that deletes</strong> — an expired dataset is erased from the server,
            file and encryption key both, with the destruction recorded. A retention commitment in
            an ethics approval is met by the system rather than by someone remembering.
          </Bullet>
          <Bullet>
            <strong>Notifications</strong> — in-app notices for request status, uploads, and
            invitations, plus broadcasts from PIs and managers.
          </Bullet>
        </Bullets>
      </Section>

      <Section id="influence" number="03" title="Write to us">
        <p>
          Priorities come from the people using the system. If something is missing for your work,
          tell us. There is no form and no ticket system to learn: a plain email to{" "}
          <Mail address={CONTACT_EMAIL} /> reaches the people who build this.
        </p>
        <div className="my-7 rounded-xl border border-accent/25 bg-accent-pale/50 px-6 py-5">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-600">
            Write to us at
          </p>
          <p className="mt-2 font-display text-2xl text-ink">
            <a href={`mailto:${CONTACT_EMAIL}`} className="underline decoration-accent/50 underline-offset-4 hover:decoration-accent">
              {CONTACT_EMAIL}
            </a>
          </p>
          <div className="site-prose mt-4 text-[0.9375rem]">
            <p className="mb-2">Any of these is welcome:</p>
          </div>
          <Bullets tight>
            <Bullet>
              <strong>Suggestions and feature requests</strong> — what would make the system fit
              the way your group actually works.
            </Bullet>
            <Bullet>
              <strong>Bug reports</strong> — what you did, what you expected, and what happened
              instead. A screenshot and the rough time help us find it in the logs.
            </Bullet>
            <Bullet>
              <strong>Requests for a new department</strong> — if your department, institute, or
              research unit would like its own separate space in the system, say who will act as
              its manager and we will set it up.
            </Bullet>
            <Bullet>
              <strong>Questions before you commit data</strong> — about the security model, what
              the system does not yet do, or what we could commit to for your ethics application.
            </Bullet>
          </Bullets>
        </div>
        <p>
          Your project&rsquo;s principal investigator and your department&rsquo;s manager can also
          bring things to us, and remain the fastest route for anything about your own account or
          access.
        </p>
        <p>
          For how the system works day to day, see{" "}
          <Link to="/about" className="text-accent underline underline-offset-2">
            About
          </Link>
          ; for how it is protected, see{" "}
          <Link to="/security" className="text-accent underline underline-offset-2">
            Security
          </Link>
          .
        </p>
      </Section>
    </div>
  </LongPage>
);
